import classNames from "classnames";
import React, { Fragment } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import APR from "Shared/APR";
import LoadingBar from "Shared/LoadingBar";
import PaymentDisclaimer from "Shared/PaymentDisclaimer";
import { Mobile, NotMobile } from "Shared/Responsive";
import { getPaymentFrequencyText } from "src/utils";
import { localizeCurrencyWithDecimals } from "src/utils/";
import { logError } from "src/utils/logger";
import { AccountState, LeaseOffer, LoanOffer, PaymentTypeOptions } from "Types/state";
import "./CalculatedPayments.scss";
import { LOADING_STATUS, monthlyPaymentDisplay, NO_OFFERS_STATUS, PaymentData } from "./lib";
import NoOffers from "./NoOffers";
import styled, { css } from "styled-components";
import ArrowRightIcon from "src/components/icons/ArrowRight";
import "src/components/ContinueDeal/VehicleInfo/VehicleInfo.scss";

export interface CalculatedPaymentProps {
    account: AccountState;
    cashLabel?: string;
    className?: string;
    displayCashLabel?: boolean;
    displayDealerNumber?: boolean;
    displayPaymentInfo?: boolean;
    inCustomizePayment?: boolean;
    inGarage?: boolean;
    isPaymentsLoading?: boolean;
    offerIndex?: number;
    payments: any;
    phoneNumber: string;
    selectedPaymentIndex: PaymentTypeOptions;
    showAPR?: boolean;
    vin: string;
    innerRef?: React.RefObject<HTMLSpanElement>;
    isTaxesCustomerRequested?: boolean;
    openPaymentPlanReviewModal?: (vin: string) => void;
    viewPlanButtonLabel?: string;
}

interface State {
    paymentData?: PaymentData;
}

const IconStyle = css<{ affix: string }>`
    color: ${(props) => props.theme.palette.primary.main};
    display: inline-block;
    height: 12px;
    padding: ${(props) => (props.affix === "prefix" ? "0 4px 0 0" : "0 2px 0 2px")};
    width: 11px;
`;

const ArrowRight = styled(ArrowRightIcon)<{ affix: string }>`
    ${IconStyle};
`;

class CalculatedPayment extends React.PureComponent<CalculatedPaymentProps & WithTranslation, State> {
    public readonly state: Readonly<State> = {
        paymentData: undefined,
    };
    private loadingTimeout?: number;

    public componentDidMount() {
        this.computeMonthlyPaymentDisplay();
    }

    public componentWillUnmount() {
        this.clearLoadingTimeout();
    }

    public componentDidUpdate(
        prevProps: Readonly<CalculatedPaymentProps & WithTranslation>,
        prevState: Readonly<State>
    ) {
        this.computeMonthlyPaymentDisplay(prevState.paymentData);
    }

    public handleCustomizedPaymentViewClick = () => {
        this.props.openPaymentPlanReviewModal && this.props.openPaymentPlanReviewModal(this.props.vin);
    };

    public render() {
        const {
            selectedPaymentIndex,
            inCustomizePayment,
            inGarage,
            isPaymentsLoading,
            offerIndex,
            phoneNumber = "",
            vin,
            cashLabel = this.props.t("CalculatedPayment.cashLabel"),
            displayDealerNumber = false,
            displayPaymentInfo = true,
            displayCashLabel = true,
            className,
            showAPR = true,
            isTaxesCustomerRequested,
            viewPlanButtonLabel = this.props.t("ContinueDeal.VehicleInfo.viewPlanButtonLabel"),
        } = this.props;
        const { paymentData } = this.state;

        if (!paymentData) {
            return null;
        }

        if (paymentData.status || isPaymentsLoading) {
            if (paymentData.status === LOADING_STATUS || isPaymentsLoading) {
                return (
                    <span
                        ref={this.props.innerRef}
                        className={classNames("CalculatedPayment__loading-container", className)}
                        data-e2e="calculatedPaymentLoader"
                    >
                        <LoadingBar />
                    </span>
                );
            }

            if (paymentData.status === NO_OFFERS_STATUS) {
                return (
                    <span ref={this.props.innerRef} className={classNames("CalculatedPayment__NoOffer", className)}>
                        <NoOffers
                            displayDealerNumber={displayDealerNumber}
                            displayTooltip={inGarage}
                            paymentType={selectedPaymentIndex}
                            dealerNumber={phoneNumber}
                        />
                    </span>
                );
            }

            return null;
        }

        const { paymentInfoString: paymentInfo, paymentOffers, selectedOffer } = paymentData;
        const displayCash = selectedPaymentIndex === "cash";
        const selectedPaymentOffer = paymentOffers && paymentOffers[selectedOffer];
        const selectedLoanOrLeasePaymentOffer = selectedPaymentOffer as LoanOffer | LeaseOffer;

        if (!displayCash && selectedLoanOrLeasePaymentOffer && selectedLoanOrLeasePaymentOffer.payment === 0) {
            logError("offer with payment 0", selectedPaymentOffer);

            return (
                <span ref={this.props.innerRef} className={classNames("CalculatedPayment__NoOffer", className)}>
                    <NoOffers
                        dealerNumber={phoneNumber}
                        displayDealerNumber={displayDealerNumber}
                        displayTooltip={inGarage}
                        paymentType={selectedPaymentIndex}
                    />
                </span>
            );
        }

        let paymentInfoOrUserAppliedTaxes;
        if (isTaxesCustomerRequested) {
            paymentInfoOrUserAppliedTaxes = (
                <span className="CalculatedPayment__container--info">
                    <span className="CalculatedPayment__info" data-e2e="calculatedPaymentInfo">
                        <button
                            className="VehicleInfo__button VehicleInfo__button--bold VehicleInfo__button--view-payment-plan"
                            onClick={this.handleCustomizedPaymentViewClick}
                        >
                            {viewPlanButtonLabel}
                            <ArrowRight affix="suffix" />
                        </button>
                    </span>
                </span>
            );
        } else {
            paymentInfoOrUserAppliedTaxes = (
                <span className="CalculatedPayment__container--info">
                    <span className="CalculatedPayment__info" data-e2e="calculatedPaymentInfo">
                        {paymentInfo}
                    </span>
                </span>
            );
        }

        const additionalDisclaimer = inCustomizePayment ? (
            <Fragment>{paymentInfoOrUserAppliedTaxes}</Fragment>
        ) : undefined;
        return (
            <span ref={this.props.innerRef} className={classNames("CalculatedPayment__container", className)}>
                {!displayCash && (
                    <>
                        <span className="CalculatedPayment__container--payment">
                            <span className="CalculatedPayment__payment">
                                {selectedLoanOrLeasePaymentOffer &&
                                    localizeCurrencyWithDecimals(selectedLoanOrLeasePaymentOffer.payment)}
                            </span>
                            <span className="CalculatedPayment__copy">
                                {` ${
                                    selectedLoanOrLeasePaymentOffer &&
                                    getPaymentFrequencyText(selectedLoanOrLeasePaymentOffer.frequency)
                                }`}
                                <PaymentDisclaimer
                                    vin={vin}
                                    offerIndex={offerIndex}
                                    additionalDisclaimer={additionalDisclaimer}
                                />
                            </span>
                        </span>
                        {showAPR && <APR vin={vin} offerIndex={offerIndex} />}
                    </>
                )}
                {displayCash && (
                    <span className="CalculatedPayment__container--cash">
                        {!inCustomizePayment && displayCashLabel && (
                            <Mobile>
                                <span className="CalculatedPayment__copy--cash">{cashLabel}</span>
                            </Mobile>
                        )}
                        {displayCashLabel && (
                            <NotMobile>
                                <span className="CalculatedPayment__copy--cash">{cashLabel}</span>
                            </NotMobile>
                        )}
                        <span className="CalculatedPayment__payment">
                            {selectedPaymentOffer &&
                                selectedPaymentOffer.amount_financed &&
                                localizeCurrencyWithDecimals(selectedPaymentOffer.amount_financed)}
                        </span>
                        <PaymentDisclaimer
                            vin={vin}
                            offerIndex={offerIndex}
                            additionalDisclaimer={additionalDisclaimer}
                        />
                    </span>
                )}
                {displayPaymentInfo && paymentInfo && <>{paymentInfoOrUserAppliedTaxes}</>}
            </span>
        );
    }

    private computeMonthlyPaymentDisplay(prevPaymentData?: PaymentData) {
        const { account, selectedPaymentIndex, payments, offerIndex } = this.props;

        if (payments) {
            const paymentData = monthlyPaymentDisplay(account, payments, selectedPaymentIndex, offerIndex);
            const shouldUpdate =
                !prevPaymentData ||
                (paymentData && Object.keys(prevPaymentData).some((k) => prevPaymentData[k] !== paymentData[k]));
            if (shouldUpdate) {
                this.setState({
                    paymentData,
                });

                if (paymentData?.status !== LOADING_STATUS && !this.props.isPaymentsLoading) {
                    window.dispatchEvent(new CustomEvent("calculatedPaymentUpdated"));
                }
            }

            this.shouldSetLoadingTimeout(paymentData);
        }
    }

    private shouldSetLoadingTimeout(paymentData: PaymentData | undefined) {
        const newStatusLoading = paymentData && paymentData.status && paymentData.status === LOADING_STATUS;

        if (newStatusLoading) {
            this.timeoutLoadingStatus();
        }
    }

    private timeoutLoadingStatus() {
        this.clearLoadingTimeout();

        this.loadingTimeout = setTimeout(() => {
            const { paymentData } = this.state;

            const loadingStatus = paymentData && paymentData.status && paymentData.status === LOADING_STATUS;

            if (loadingStatus) {
                this.setState({
                    paymentData: { status: NO_OFFERS_STATUS },
                });
            }

            this.loadingTimeout = undefined;
        }, 15 * 1000);
    }

    private clearLoadingTimeout() {
        if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
        }
    }
}

export default withTranslation()(CalculatedPayment);
